/**
 * When the expanded card panel may replace what is on screen with what the server holds.
 *
 * The panel renders its fields from a `draft`, seeded from the note once. Seeding once is
 * deliberate: a media write re-fetches the note, and re-seeding there would discard text typed
 * since. But "once" was too strong — an APPEND rewrites the note the panel is already showing,
 * and `noteId` does not change, so nothing told the panel to look again. Pressing add on an
 * archaic ねこま row while the 猫 card was open wrote the new section to the collection and left
 * the panel showing the card as it was before, which reads as the append having failed.
 *
 * Three rules, and they interact — which is why this is a function rather than a condition inlined
 * at the call site:
 *
 *  1. **Nothing seeded yet → seed.** The ordinary first load.
 *  2. **The server rewrote this note → re-seed.** What is on screen is not an edit in progress,
 *     it is a stale copy, and leaving it there hides the write.
 *  3. **...unless there is unsaved typing, which wins.** An append is worth showing; it is not
 *     worth losing a half-written definition for. Worse than losing it: `save()` posts the whole
 *     draft, so a stale draft left in place would write the pre-append text straight back over
 *     the append.
 */
export function shouldSeedDraft(state: {
  /** Has the draft been populated at all yet? */
  seeded: boolean;
  /** Did this load come from a `revision` bump — i.e. did the server rewrite the note? */
  rewrittenByServer: boolean;
  /** Are there edits in the draft that have not been saved? */
  dirty: boolean;
}): boolean {
  if (!state.seeded) return true;
  if (!state.rewrittenByServer) return false;
  return !state.dirty;
}
